import { createContext, useContext, useReducer } from "react";
import type { Dispatch, ReactNode } from "react";

/**
 * Reference provider for the US1994902 UI rewrite. The track-owned providers
 * (InventoryProvider, SelectionsProvider, ChoicesetProvider, …) are modeled on
 * this typed `createContext` + `useReducer` pattern: a discriminated-union
 * action type, an exhaustive reducer, and a consumer hook that throws when used
 * outside the provider.
 */
export interface InventoryManagerState {
  searchValue: string;
  selectedVehicleUuid: string | null;
  isLoading: boolean;
}

export type InventoryManagerAction =
  | { type: "SET_SEARCH_VALUE"; searchValue: string }
  | { type: "SELECT_VEHICLE"; uuid: string | null }
  | { type: "SET_LOADING"; isLoading: boolean };

export const initialInventoryManagerState: InventoryManagerState = {
  searchValue: "",
  selectedVehicleUuid: null,
  isLoading: false
};

export function inventoryManagerReducer(
  state: InventoryManagerState,
  action: InventoryManagerAction
): InventoryManagerState {
  switch (action.type) {
    case "SET_SEARCH_VALUE":
      return { ...state, searchValue: action.searchValue };
    case "SELECT_VEHICLE":
      return { ...state, selectedVehicleUuid: action.uuid };
    case "SET_LOADING":
      return { ...state, isLoading: action.isLoading };
    default:
      return state;
  }
}

export interface InventoryManagerContextValue {
  state: InventoryManagerState;
  dispatch: Dispatch<InventoryManagerAction>;
}

export const InventoryManagerContext =
  createContext<InventoryManagerContextValue | null>(null);

export interface InventoryManagerProviderProps {
  children: ReactNode;
  initialState?: InventoryManagerState;
}

export const InventoryManagerProvider = ({
  children,
  initialState = initialInventoryManagerState
}: InventoryManagerProviderProps) => {
  const [state, dispatch] = useReducer(inventoryManagerReducer, initialState);

  return (
    <InventoryManagerContext.Provider value={{ state, dispatch }}>
      {children}
    </InventoryManagerContext.Provider>
  );
};

export function useInventoryManagerContext(): InventoryManagerContextValue {
  const context = useContext(InventoryManagerContext);
  if (context === null) {
    throw new Error(
      "useInventoryManagerContext must be used within an InventoryManagerProvider"
    );
  }
  return context;
}
